import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { goalkeeperService } from '../../../services/performanceService';

const GoalkeeperPerformanceForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    // Goalkeeper Profile
    fullName: '',
    age: '',
    nationality: '',
    height: '',
    // Career History
    currentTeam: '',
    previousTeams: '',
    competitions: '',
    // Goalkeeper Statistics
    gamesPlayed: '',
    gamesWon: '',
    cleanSheets: '',
    goalsConceded: '',
    minutesPlayed: '',
    savesMade: '',
    penaltiesSaved: '',
    // Transfer & Availability
    availableForTransfer: false,
    marketValue: '',
    contractStatus: '',
    // Media
    goalkeeperPhoto: null,
    matchHighlights: null
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contractStatusOptions = [
    { value: 'active', label: 'Active Contract' },
    { value: 'expiring', label: 'Expiring Soon' },
    { value: 'free-agent', label: 'Free Agent' },
    { value: 'loan', label: 'On Loan' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files?.[0] : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.fullName?.trim()) newErrors.fullName = 'Full name is required';
    if (!formData?.age) newErrors.age = 'Age is required';
    if (!formData?.nationality?.trim()) newErrors.nationality = 'Nationality is required';
    if (!formData?.height?.trim()) newErrors.height = 'Height is required';
    if (!formData?.currentTeam?.trim()) newErrors.currentTeam = 'Current team is required';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare files object
      const files = {
        goalkeeperPhoto: formData?.goalkeeperPhoto,
        matchHighlights: formData?.matchHighlights
      };
      
      // Prepare data object (exclude file objects)
      const goalkeeperData = {
        fullName: formData?.fullName,
        age: parseInt(formData?.age),
        nationality: formData?.nationality,
        height: formData?.height,
        currentTeam: formData?.currentTeam,
        previousTeams: formData?.previousTeams,
        competitions: formData?.competitions,
        gamesPlayed: parseInt(formData?.gamesPlayed) || 0,
        gamesWon: parseInt(formData?.gamesWon) || 0,
        cleanSheets: parseInt(formData?.cleanSheets) || 0,
        goalsConceded: parseInt(formData?.goalsConceded) || 0,
        minutesPlayed: parseInt(formData?.minutesPlayed) || 0,
        savesMade: parseInt(formData?.savesMade) || 0,
        penaltiesSaved: parseInt(formData?.penaltiesSaved) || 0,
        availableForTransfer: formData?.availableForTransfer,
        marketValue: formData?.marketValue,
        contractStatus: formData?.contractStatus
      };
      
      const { data, error } = await goalkeeperService?.create(goalkeeperData, files);
      
      if (error) {
        console.error('Error creating goalkeeper record:', error);
        setErrors({ submit: error?.message || 'Failed to submit goalkeeper record' });
        return;
      }
      
      onSubmit?.();
      
      // Reset form
      setFormData({
        fullName: '', age: '', nationality: '', height: '',
        currentTeam: '', previousTeams: '', competitions: '',
        gamesPlayed: '', gamesWon: '', cleanSheets: '', goalsConceded: '',
        minutesPlayed: '', savesMade: '', penaltiesSaved: '',
        availableForTransfer: false, marketValue: '', contractStatus: '',
        goalkeeperPhoto: null, matchHighlights: null
      });
    } catch (error) {
      console.error('Submit error:', error);
      setErrors({ submit: 'An unexpected error occurred' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateCleanSheetPercentage = () => {
    const games = parseInt(formData?.gamesPlayed) || 0;
    const cleanSheets = parseInt(formData?.cleanSheets) || 0;
    if (games === 0) return '0.00';
    return ((cleanSheets / games) * 100)?.toFixed(2);
  };

  const calculateAverageGoalsConceded = () => {
    const games = parseInt(formData?.gamesPlayed) || 0;
    const conceded = parseInt(formData?.goalsConceded) || 0;
    if (games === 0) return '0.00';
    return (conceded / games)?.toFixed(2);
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-8">
      {/* Goalkeeper Profile Information */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Shield" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Goalkeeper Profile Information</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            name="fullName"
            value={formData?.fullName}
            onChange={handleChange}
            placeholder="Enter full name"
            required
            error={errors?.fullName}
          />
          <Input
            label="Age"
            name="age"
            type="number"
            value={formData?.age}
            onChange={handleChange}
            placeholder="Enter age"
            required
            error={errors?.age}
          />
          <Input
            label="Nationality"
            name="nationality"
            value={formData?.nationality}
            onChange={handleChange}
            placeholder="Enter nationality"
            required
            error={errors?.nationality}
          />
          <Input
            label="Height"
            name="height"
            value={formData?.height}
            onChange={handleChange}
            placeholder={"e.g., 1.90m or 6'3\""}
            required
            error={errors?.height}
            description="Height is crucial for goalkeepers"
          />
        </div>
      </div>
      {/* Career History */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Building2" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Career History</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Current Team"
            name="currentTeam"
            value={formData?.currentTeam}
            onChange={handleChange}
            placeholder="Enter current team"
            required
            error={errors?.currentTeam}
          />
          <Input
            label="Previous Teams"
            name="previousTeams"
            value={formData?.previousTeams}
            onChange={handleChange}
            placeholder="e.g., Team A (2020-2023), Team B (2018-2020)"
            description="List previous teams with seasons"
          />
          <div className="md:col-span-2">
            <Input
              label="Competitions Played"
              name="competitions"
              value={formData?.competitions}
              onChange={handleChange}
              placeholder="e.g., Premier League, Champions League, FA Cup"
            />
          </div>
        </div>
      </div>
      {/* Goalkeeper Statistics */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="BarChart3" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Goalkeeper Statistics</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="Games Played"
            name="gamesPlayed"
            type="number"
            value={formData?.gamesPlayed}
            onChange={handleChange}
            placeholder="Total games"
          />
          <Input
            label="Games Won"
            name="gamesWon"
            type="number"
            value={formData?.gamesWon}
            onChange={handleChange}
            placeholder="Wins"
          />
          <Input
            label="Clean Sheets"
            name="cleanSheets"
            type="number"
            value={formData?.cleanSheets}
            onChange={handleChange}
            placeholder="Clean sheets"
          />
          <Input
            label="Goals Conceded"
            name="goalsConceded"
            type="number"
            value={formData?.goalsConceded}
            onChange={handleChange}
            placeholder="Goals conceded"
          />
          <Input
            label="Minutes Played"
            name="minutesPlayed"
            type="number"
            value={formData?.minutesPlayed}
            onChange={handleChange}
            placeholder="Total minutes"
          />
          <Input
            label="Saves Made"
            name="savesMade"
            type="number"
            value={formData?.savesMade}
            onChange={handleChange}
            placeholder="Total saves"
          />
          <Input
            label="Penalties Saved"
            name="penaltiesSaved"
            type="number"
            value={formData?.penaltiesSaved}
            onChange={handleChange}
            placeholder="Penalties saved"
          />
        </div>
        
        {formData?.gamesPlayed && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">Clean Sheet %</span>
                <span className="text-2xl font-bold text-primary">{calculateCleanSheetPercentage()}%</span>
              </div>
            </div>
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">Avg Goals Conceded</span>
                <span className="text-2xl font-bold text-primary">{calculateAverageGoalsConceded()}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Transfer & Availability */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="TrendingUp" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Transfer & Availability</h2>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
            <Checkbox
              id="availableForTransfer"
              checked={formData?.availableForTransfer}
              onCheckedChange={(checked) => handleSelectChange('availableForTransfer', checked)}
            />
            <label htmlFor="availableForTransfer" className="text-sm font-medium cursor-pointer">
              Available for Transfer
            </label>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Market Value"
              name="marketValue"
              value={formData?.marketValue}
              onChange={handleChange}
              placeholder="e.g., €3.5M"
            />
            <Select
              label="Contract Status"
              name="contractStatus"
              options={contractStatusOptions}
              value={formData?.contractStatus}
              onChange={(value) => handleSelectChange('contractStatus', value)}
              placeholder="Select contract status"
            />
          </div>
        </div>
      </div>
      {/* Media */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Upload" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Media</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Goalkeeper Photo"
            name="goalkeeperPhoto"
            type="file"
            onChange={handleChange}
            description="Upload professional photo"
          />
          <Input
            label="Match Highlights"
            name="matchHighlights"
            type="file"
            onChange={handleChange}
            description="Upload save highlights (optional)"
          />
        </div>
      </div>
      {/* Submit Button */}
      <div className="flex justify-end gap-4 pt-6 border-t border-border">
        <Button
          type="button"
          variant="outline"
          onClick={() => window.location?.reload()}
        >
          Reset Form
        </Button>
        <Button
          type="submit"
          loading={isSubmitting}
          iconName="Save"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Goalkeeper Record'}
        </Button>
      </div>
    </form>
  );
};

export default GoalkeeperPerformanceForm;